import React, { useState, useRef } from 'react';

import { Input } from '@zillow/constellation';

import KeyCodeConstants from 'app/shared/constants/KeyCodeConstants';
import { useOutsideClick } from 'app/shared/hooks';
import {
    StyledAutocompleteContainer,
    StyledDropdown,
    StyledDropdownItem,
    StyledDropdownItemText,
} from './StyledComponents';

const Autocomplete = ({
    className,
    disabled = false,
    items,
    onChange,
    onSelect,
    placeholder = 'Search',
    value = '',
}) => {
    const ref = useRef(null);
    const [isFocused, toggleFocus] = useState(false);
    const [activeIndex, updateActiveIndex] = useState(-1);

    useOutsideClick(ref, () => {
        toggleFocus(false);
    });

    const handleFocus = () => {
        toggleFocus(true);
    };

    const handleChange = (e) => {
        // need e.persist() so that the debounced change handler has reference to the event
        // https://reactjs.org/docs/events.html#event-pooling
        e.persist();
        onChange(e.target.value);
    };

    const handleSelect = (item) => {
        onSelect(item);
        toggleFocus(false);
    };

    const handleKeyDown = (e) => {
        const keyCode = e.which;
        toggleFocus(true);

        if (keyCode === KeyCodeConstants.UP_ARROW) {
            const upOneIndex = activeIndex === -1 ? items.length - 1 : activeIndex - 1;
            updateActiveIndex(upOneIndex);
            e.preventDefault();
        } else if (keyCode === KeyCodeConstants.DOWN_ARROW) {
            const downOneIndex = activeIndex === items.length - 1 ? -1 : activeIndex + 1;
            updateActiveIndex(downOneIndex);
            e.preventDefault();
        } else if (keyCode === KeyCodeConstants.ESC) {
            toggleFocus(false);
        } else if (keyCode === KeyCodeConstants.TAB) {
            toggleFocus(false);
        } else if (keyCode === KeyCodeConstants.ENTER) {
            e.preventDefault();
            // if no activeIndex, default to first selection
            const selectedItem = activeIndex === -1 ? items[0] : items[activeIndex];
            if (selectedItem) {
                handleSelect(selectedItem);

                e.target.blur();
            } else {
                // user did not select an option
            }
        }
    };

    const renderDropdownItem = ({
        idx,
        item,
    }) => (
        <StyledDropdownItem
            isActive={idx === activeIndex}
            key={idx}
            onClick={() => handleSelect(items[idx])}
            onMouseEnter={() => updateActiveIndex(idx)}
            onMouseMove={() => updateActiveIndex(idx)}
        >
            <StyledDropdownItemText key={idx}>
                {item.name}
            </StyledDropdownItemText>
        </StyledDropdownItem>
    );

    const renderDropdown = () => {
        if (!isFocused) {
            return null;
        } if (items.length) {
            return <StyledDropdown>{items.map((item, idx) => renderDropdownItem({ item, idx }))}</StyledDropdown>;
        }
        return null;
    };

    /**
     * Note about disabling Google Chrome's autocomplete:
     * autocomplete="off || false" prop on <input> should work, however Constellation's
     * Input does not pass `...rest` to the <input>. Luckily, you can disable
     * autocomplete for an entire form in the same way.
     * The 'data-' prefix used to embed custom attributes on HTML elements and fix typescript errors.
     */
    return (
        <StyledAutocompleteContainer className={className}>
            <form ref={ref} data-autocomplete="off">
                <Input
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    value={value}
                    disabled={disabled}
                />

                {renderDropdown()}
            </form>
        </StyledAutocompleteContainer>
    );
};

export default Autocomplete;
